import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

const TRUST_MESSAGES = [
  { text: "97% Reorder Rate", emoji: "🔥" },
  { text: "72h Delivery", emoji: "⚡" },
  { text: "250+ Businesses", emoji: "🚀" },
  { text: "850K+ Views Generated", emoji: "👁️" },
];

// Circular Progress Ring Component
const CircularProgress = ({ progress }: { progress: number }) => {
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <svg width="40" height="40" className="transform -rotate-90">
      {/* Background circle */}
      <circle
        cx="20"
        cy="20"
        r={radius}
        fill="none"
        stroke="hsl(var(--muted) / 0.3)"
        strokeWidth="3"
      />
      {/* Progress circle */}
      <circle
        cx="20"
        cy="20"
        r={radius}
        fill="none"
        stroke="url(#progressGradient)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        className="transition-all duration-300 ease-out"
      />
      {/* Gradient definition */}
      <defs>
        <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="hsl(var(--neon-purple))" />
          <stop offset="100%" stopColor="hsl(var(--neon-pink))" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const DesktopFloatingCTA = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showPulse, setShowPulse] = useState(false);
  const [price, setPrice] = useState(375);
  const ctaRef = useRef<HTMLDivElement>(null);
  const collapseTimeoutRef = useRef<NodeJS.Timeout>();
  const ticking = useRef(false);

  // Get price from localStorage or default
  useEffect(() => {
    const savedPrice = localStorage.getItem('fuel72_selected_price');
    if (savedPrice) {
      setPrice(parseInt(savedPrice));
    }

    // Listen for price changes
    const handleStorage = () => {
      const newPrice = localStorage.getItem('fuel72_selected_price');
      if (newPrice) setPrice(parseInt(newPrice));
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Scroll progress tracking with RAF throttling
  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / scrollHeight) * 100;
      setScrollProgress(Math.min(progress, 100));

      // Show pulse when passing pricing section
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) {
        const rect = pricingSection.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          setShowPulse(true);
          setTimeout(() => setShowPulse(false), 2000);
        }
      }
      
      ticking.current = false;
    };

    const handleScroll = () => {
      if (!ticking.current) {
        requestAnimationFrame(updateScrollProgress);
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Trust message rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % TRUST_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Auto-collapse after 5 seconds, expand on hover
  useEffect(() => {
    if (!isHovered) {
      collapseTimeoutRef.current = setTimeout(() => {
        setIsCollapsed(true);
      }, 5000);
    } else {
      setIsCollapsed(false);
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current);
      }
    }

    return () => {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current);
      }
    };
  }, [isHovered]);

  // Magnetic effect - subtle follow cursor
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ctaRef.current) return;
    const rect = ctaRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const deltaX = (e.clientX - centerX) * 0.08;
    const deltaY = (e.clientY - centerY) * 0.08;
    setMousePosition({ x: deltaX, y: deltaY });
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 0, y: 0 });
    setIsHovered(false);
  };

  const currentMessage = TRUST_MESSAGES[currentMessageIndex];

  return (
    <div
      ref={ctaRef}
      className={cn(
        "fixed bottom-8 right-8 z-50 hidden lg:flex transition-all duration-500 ease-out",
        isCollapsed && !isHovered ? "scale-90" : "scale-100"
      )}
      style={{
        transform: `translate(${mousePosition.x}px, ${mousePosition.y}px) translateZ(0)`,
        contain: 'layout style',
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={cn(
          "relative flex items-center gap-3 glass-morphism-enhanced backdrop-blur-lg",
          "border border-white/20 rounded-full shadow-2xl",
          "transition-all duration-500 ease-out overflow-hidden",
          isCollapsed && !isHovered ? "px-3 py-3" : "px-4 py-3",
          showPulse && "animate-desktop-cta-pulse",
          isHovered && "border-neon-purple/50 shadow-neon-purple/30"
        )}
      >
        {/* Glow background */}
        <div className={cn(
          "absolute inset-0 bg-gradient-to-r from-neon-purple/20 via-neon-pink/10 to-neon-cyan/20 rounded-full blur-xl transition-opacity duration-500",
          isHovered ? "opacity-100" : "opacity-50"
        )} />

        {/* Content */}
        <div className="relative z-10 flex items-center gap-3">
          {/* Scroll Progress Ring */}
          <div className="relative flex-shrink-0">
            <CircularProgress progress={scrollProgress} />
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white/80">
              {Math.round(scrollProgress)}%
            </span>
          </div>

          {/* Expanded content */}
          <div
            className={cn(
              "flex items-center gap-3 transition-all duration-500 ease-out overflow-hidden",
              isCollapsed && !isHovered ? "w-0 opacity-0" : "w-auto opacity-100"
            )}
          >
            {/* Trust ticker */}
            <div className="flex items-center gap-1.5 min-w-[140px]">
              <span className="text-base animate-bounce-subtle">{currentMessage.emoji}</span>
              <span
                key={currentMessageIndex}
                className="text-xs font-medium text-white/90 whitespace-nowrap animate-fade-in"
              >
                {currentMessage.text}
              </span>
            </div>

            {/* CTA Button */}
            <Button
              onClick={() => window.location.href = '/order'}
              className={cn(
                "relative bg-gradient-to-r from-neon-purple via-neon-pink to-neon-purple",
                "hover:shadow-lg hover:shadow-neon-purple/40 hover:scale-105",
                "text-white font-bold text-sm rounded-full px-5 py-2 h-auto",
                "transition-all duration-300 group overflow-hidden"
              )}
            >
              {/* Shimmer effect */}
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="relative flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                €{price}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Button>
          </div>

          {/* Collapsed icon */}
          {isCollapsed && !isHovered && (
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-neon-purple to-neon-pink animate-pulse-glow">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesktopFloatingCTA;
